import {
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import { PlanId } from "components/common/boundary/PlanId";
import { Period } from "hooks/periodIndex";

import { RootState } from "store/store.tsx";

type View = "deploy" | "backup" | "supervisor";

interface SteelGradeSearch {
  planId: PlanId;
  period: Period;
  view: View;
  steelGradeIds: number[];
}

const makeId = (planId: PlanId, period: Period, view: View) =>
  [planId, period, view].join(".");

const steelGradeSearchAdapter = createEntityAdapter({
  selectId: (steelGradeSearch: SteelGradeSearch) =>
    makeId(
      steelGradeSearch.planId,
      steelGradeSearch.period,
      steelGradeSearch.view
    ),
});

const adapterSelectors = steelGradeSearchAdapter.getSelectors();

export const steelGradeSearchSlice = createSlice({
  name: "steelGradeSearch",
  initialState: steelGradeSearchAdapter.getInitialState(undefined, []),
  reducers: {
    setSteelGrades: (
      state,
      {
        payload: [planId, period, steelGradeIds, view],
      }: PayloadAction<[PlanId, Period, number[], View]>
    ) => {
      steelGradeSearchAdapter.setOne(state, {
        planId,
        period,
        view,
        steelGradeIds,
      });
    },
    clear: (
      state,
      { payload: [planId, period, view] }: PayloadAction<[PlanId, Period, View]>
    ) => {
      steelGradeSearchAdapter.removeOne(state, makeId(planId, period, view));
    },
    copy(
      state,
      {
        payload: [fromPlanId, fromPeriod, view, toPlanId, toPeriod],
      }: PayloadAction<[PlanId, Period, View, PlanId, Period]>
    ) {
      const fromPlanSearchedGrades = adapterSelectors.selectById(
        state,
        makeId(fromPlanId, fromPeriod, view)
      );
      if (fromPlanSearchedGrades) {
        steelGradeSearchAdapter.setOne(state, {
          planId: toPlanId,
          period: toPeriod,
          view,
          steelGradeIds: fromPlanSearchedGrades.steelGradeIds,
        });
      }
    },
  },
});

const createAppSelector = createSelector.withTypes<RootState>();

export const selectSearchedSteelGradeIds = createAppSelector(
  [
    (state) => state.steelGradeSearch,
    (_, planId: PlanId) => planId,
    (_, __, period: Period) => period,
    (_, __, ___, view: View) => view,
  ],
  (steelGradeSearch, planId, period, view) =>
    steelGradeSearchAdapter
      .getSelectors()
      .selectById(steelGradeSearch, makeId(planId, period, view))
      ?.steelGradeIds ?? []
);
